import React, { useState } from 'react';
import { Post } from '../../types/interfaces';
import { convertToProxyUrl } from '../../utils/photoProxy';
import { cn } from '../../spreadsheet/src/utils/cn';
import { useUserDetails, useCopyToClipboard, usePostMedia } from '../../hooks';
import DisplayJSON from '../DisplayJSON/DisplayJSON';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CodeIcon from '@mui/icons-material/Code';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

export interface PostDetailsProps {
  isOpen: boolean;
  onClose: () => void;
  post: Post | null;
}

const PostDetails: React.FC<PostDetailsProps> = ({
  isOpen,
  onClose,
  post
}) => {
  const [showJsonDialog, setShowJsonDialog] = useState(false);
  const [localPost, setLocalPost] = useState<Post | null>(post);
  const copyToClipboard = useCopyToClipboard();

  // Get user details using hook (must be called before early return)
  const { displayName, username, accountAge } = useUserDetails({
    user: (localPost?.user ?? null) as Post['user'] | null,
    enabled: isOpen && !!localPost,
  });

  // Post media hook
  const {
    handleFileSelect,
    handleDeleteMedia,
    triggerFileInput,
    fileInputRef,
    isUploading,
    isDeleting,
  } = usePostMedia({
    postId: localPost?._id || '',
    onUpdate: (updatedPost) => {
      setLocalPost(updatedPost);
    },
    onError: (error) => {
      console.error('Media operation error:', error);
    },
  });

  // Update local post when prop changes
  React.useEffect(() => {
    setLocalPost(post);
  }, [post]);

  if (!isOpen || !localPost) return null;

  const getMediaUrl = () => {
    if (!localPost.media) return null;
    if (localPost.media.type === 'photo' && localPost.media.photo) {
      return convertToProxyUrl(localPost.media.photo);
    }
    if (localPost.media.type === 'video' && localPost.media.video) {
      return convertToProxyUrl(localPost.media.video);
    }
    if (localPost.media.thumbnail) {
      return convertToProxyUrl(localPost.media.thumbnail);
    }
    return null;
  };

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-gray-900">Post Details</h2>
            <div className="flex items-center gap-2">
              <code className="text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">{localPost._id}</code>
              <button
                onClick={() => copyToClipboard(localPost._id)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                title="Copy Post ID"
              >
                <ContentCopyIcon fontSize="small" />
              </button>
              <button
                onClick={() => setShowJsonDialog(true)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                title="View Full Post JSON"
              >
                <CodeIcon fontSize="small" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-600">
              <span className="font-medium">Created:</span>{' '}
              <span className="text-gray-900">{formatDate(localPost.createdAt)}</span>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            >
              ×
            </button>
          </div>
        </div>

        {/* Location Hierarchy Strip */}
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2 flex-wrap">
            {(['place', 'neighborhood', 'city', 'state', 'country'] as const).map((level, index) => {
              const loc = localPost.location[level];
              const icons: Record<string, string> = {
                place: '📍',
                neighborhood: '🏘️',
                city: '🌆',
                state: '🗺️',
                country: '🌎'
              };
              const icon = icons[level] || '📍';
              const currentPostLevelLower = localPost.currentPostLevel?.toLowerCase() || '';
              const isCurrentPostLevel = currentPostLevelLower === level;
              const hasLocation = loc && typeof loc === 'object' && 'name' in loc;

              if (!hasLocation) {
                return (
                  <React.Fragment key={level}>
                    <span className="text-gray-400 text-sm">{icon} {level.charAt(0).toUpperCase() + level.slice(1)}</span>
                    {index < 4 && <span className="text-gray-300">→</span>}
                  </React.Fragment>
                );
              }

              return (
                <React.Fragment key={level}>
                  <div
                    className={cn(
                      'px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5',
                      isCurrentPostLevel
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300'
                    )}
                  >
                    <span>{icon}</span>
                    <span>{loc.name}</span>
                    {isCurrentPostLevel && (
                      <span className={cn(
                        'w-2 h-2 rounded-full',
                        isCurrentPostLevel ? 'bg-white' : 'bg-blue-600'
                      )} title="Current post level" />
                    )}
                  </div>
                  {index < 4 && <span className="text-gray-400">→</span>}
                </React.Fragment>
              );
            })}
          </div>
          {/* Level and User Info */}
          <div className="mt-2 pt-2 border-t border-gray-200 flex items-center justify-between text-sm">
            {/* Location Details - Left */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-gray-600 font-medium">Origin Level:</span>
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                  {localPost.originLevel}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600 font-medium">Current Level:</span>
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                  {localPost.currentPostLevel}
                </span>
              </div>
            </div>
            {/* User Details - Right */}
            <div className="flex items-center gap-4">
              <div>
                <span className="text-gray-600 font-medium">User:</span>{' '}
                <span className="text-gray-900 font-medium">{displayName}</span>
              </div>
              <div>
                <span className="text-gray-600 font-medium">Username:</span>{' '}
                <span className="text-gray-900">@{username}</span>
              </div>
              <div>
                <span className="text-gray-600 font-medium">Member for:</span>{' '}
                <span className="text-gray-900">{accountAge || '—'}</span>
              </div>
            </div>
          </div>
          {/* Text Content */}
          {(localPost.text || localPost.content) && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <p className="text-gray-900 whitespace-pre-wrap text-sm leading-relaxed">{localPost.text || localPost.content}</p>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Pane - Media */}
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Media</h3>
                <div className="relative group">
                  {localPost.media && getMediaUrl() ? (
                    <>
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        {localPost.media.type === 'video' ? (
                          <video
                            src={getMediaUrl() || undefined}
                            controls
                            className="w-full h-auto max-h-96 object-contain bg-gray-100"
                          />
                        ) : (
                          <img
                            src={getMediaUrl() || undefined}
                            alt="Post media"
                            className="w-full h-auto max-h-96 object-contain bg-gray-100"
                          />
                        )}
                        {localPost.media.width && localPost.media.height && (
                          <div className="px-3 py-2 bg-gray-50 text-xs text-gray-600 border-t border-gray-200">
                            {localPost.media.width} × {localPost.media.height} (Aspect: {localPost.media.aspectRatio?.toFixed(2)})
                          </div>
                        )}
                      </div>
                      {/* Overlay with actions */}
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity rounded-lg">
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={triggerFileInput}
                            disabled={isUploading || isDeleting}
                            className="p-2 bg-white text-gray-700 rounded hover:bg-gray-100 transition-colors disabled:opacity-50"
                            title="Edit media"
                          >
                            <EditIcon fontSize="small" />
                          </button>
                          <button
                            onClick={handleDeleteMedia}
                            disabled={isUploading || isDeleting}
                            className="p-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50"
                            title="Delete media"
                          >
                            <DeleteIcon fontSize="small" />
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div
                      onClick={triggerFileInput}
                      className="p-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 text-center text-gray-500 text-sm cursor-pointer hover:border-gray-400 hover:bg-gray-100 transition-colors"
                    >
                      {isUploading ? 'Uploading...' : 'Click to add media'}
                    </div>
                  )}
                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleFileSelect(file);
                      }
                      // Reset input so same file can be selected again
                      if (e.target) {
                        e.target.value = '';
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Right Pane - Other Info */}
            <div className="space-y-4">
              {/* Engagement Stats */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Engagement</h3>
                <div className="p-3 bg-gray-50 rounded-lg space-y-1 text-sm">
                  <div><span className="font-medium">Total Promotes:</span> {localPost.totalPromotes || 0}</div>
                  <div><span className="font-medium">Level Promotes:</span> {localPost.levelPromotes || 0}</div>
                  <div><span className="font-medium">Promote %:</span> {localPost.promotePercentage?.toFixed(1) || '0.0'}%</div>
                  <div><span className="font-medium">Comments:</span> {Array.isArray(localPost.comments) ? localPost.comments.length : 0}</div>
                  <div><span className="font-medium">Shares:</span> {localPost.shares || 0}</div>
                  <div><span className="font-medium">Interaction Score:</span> {localPost.interactionScore || 0}</div>
                  <div className="pt-1 mt-1 border-t border-gray-200" />
                  <div><span className="font-medium">Feed impressions:</span> {localPost.impressionCount || 0}</div>
                  <div><span className="font-medium">Dwell views (3s+):</span> {localPost.viewCount || 0}</div>
                  <div><span className="font-medium">Detail opens:</span> {localPost.detailOpenCount || 0}</div>
                  <div><span className="font-medium">Photo opens:</span> {localPost.photoOpenCount || 0}</div>
                  <div><span className="font-medium">Video opens:</span> {localPost.videoOpenCount || 0}</div>
                </div>
              </div>

              {/* Metadata */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Metadata</h3>
                <div className="p-3 bg-gray-50 rounded-lg space-y-1 text-sm">
                  <div><span className="font-medium">Visible:</span> {localPost.isVisible ? 'Yes' : 'No'}</div>
                  <div><span className="font-medium">Pinned:</span> {localPost.isPinned ? 'Yes' : 'No'}</div>
                  <div><span className="font-medium">Is Comment:</span> {localPost.isComment ? 'Yes' : 'No'}</div>
                  {localPost.isMockPost && (
                    <div><span className="font-medium">Mock Post:</span> Yes</div>
                  )}
                  {localPost.showUserNeighborhood !== undefined && (
                    <div><span className="font-medium">Show User Neighborhood:</span> {localPost.showUserNeighborhood ? 'Yes' : 'No'}</div>
                  )}
                </div>
              </div>

              {/* Hashtags */}
              {localPost.hashtags && localPost.hashtags.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Hashtags</h3>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex flex-wrap gap-2">
                      {localPost.hashtags.map((tag, idx) => (
                        <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Visibility Levels */}
              {localPost.visibilityLevels && localPost.visibilityLevels.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Visibility Levels</h3>
                  <div className="p-3 bg-gray-50 rounded-lg space-y-1 text-sm">
                    {localPost.visibilityLevels.map((vl, idx) => (
                      <div key={idx}>
                        <span className="font-medium">{vl.level}:</span> {vl.locationId}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {/* JSON Dialog */}
      <DisplayJSON
        open={showJsonDialog}
        onClose={() => setShowJsonDialog(false)}
        data={localPost}
        title="Post JSON Details"
        subtitle={localPost.text || localPost._id}
      />
    </div>
  );
};

export default PostDetails;
